import { APIGet, APIPost, APIPut, APIDelete, IAPIResponse, APIUrl, AdminType } from "api/protocols";
import { CancelToken } from "axios";

export type TInventoryCount = {
  created_at: string;
  created_by: number;
  created_by_full_name: string;
  date_created: string;
  date_submitted: string;
  deleted_at: string;
  facility_id: number;
  id: number;
  name: string;
  number: number;
  status: string;
  scope: "partial" | "full";
  submitted_by: number;
  updated_at: string;
};

export type TInventoryCountVariants = {
  adjustment_processed: number;
  adjustment_processed_at: string;
  barcode: string;
  created_at: string;
  cost: number;
  deleted_at: string;
  id: number;
  inventory_count_id: number;
  product_id: number;
  product_subtitle: string;
  product_title: string;
  product_vendor_title: string;
  quantity_available_at_submission: number;
  quantity_counted: number;
  quantity_difference: number;
  quantity_sold: number;
  sku: string;
  updated_at: string;
  variant_id: number;
  variant_title: string;
};

export async function GetInventoryCount(
  params: { id?: number; offset?: number; limit?: number },
  useGlobalLoader: boolean,
  token?: CancelToken,
): Promise<IAPIResponse<Array<TInventoryCount>>> {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/product/inventory/count"), params, useGlobalLoader, token);
}

export async function PostInventoryCount(
  params: { scope: TInventoryCount["scope"] },
  useGlobalLoader: boolean,
): Promise<Omit<IAPIResponse<TInventoryCount>, "message">> {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/product/inventory/count"), params, useGlobalLoader);
}

export async function GetInventoryCountVariants(
  params: { inventory_count_id?: number },
  useGlobalLoader: boolean,
  token?: CancelToken,
): Promise<IAPIResponse<Array<TInventoryCountVariants>>> {
  return await APIGet(
    APIUrl(AdminType.FacilityAdmin, "/product/inventory/count/variant"),
    params,
    useGlobalLoader,
    token,
  );
}

export async function PostInventoryCountVariants(
  params: { inventory_count_id: number; variants: Array<{ variant_id: number; quantity_counted: number }> },
  useGlobalLoader: boolean,
): Promise<Omit<IAPIResponse<TInventoryCount>, "message">> {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/product/inventory/count/variant"), params, useGlobalLoader);
}

export async function DeleteInventoryCountVariants(
  params: { inventory_count_id: number; variants: Array<number> },
  useGlobalLoader: boolean,
) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/product/inventory/count/variant"), params, useGlobalLoader);
}

export async function PutSubmitInventoryCount(
  params: { inventory_count_id: number; full_count_confirmed: boolean },
  useGlobalLoader: boolean,
) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/product/inventory/count/submit"), params, useGlobalLoader);
}

export async function PutExportInventoryCount(params: { inventory_count_id: number }, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/product/inventory/count/export"), params, useGlobalLoader);
}

export async function PutPrintInventoryCount(params: { inventory_count_id: number }, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/product/inventory/count/print"), params, useGlobalLoader);
}

export async function PutVoidInventoryCount(params: { inventory_count_id: number }, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/product/inventory/count/void"), params, useGlobalLoader);
}

export async function PutDownloadInventoryCount(params: { inventory_count_id: number }, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/product/inventory/count/download"), params, useGlobalLoader);
}
